import { useState, useEffect } from 'react';
import { db, storage } from '@/lib/firebase';
import { useTranslation } from 'react-i18next';
import { 
  collection, 
  addDoc, 
  updateDoc,
  deleteDoc, 
  doc, 
  onSnapshot,
  query,
  orderBy
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  Plus, 
  Trash2, 
  X, 
  Image as ImageIcon, 
  Loader2, 
  Upload,
  Tag,
  Pencil,
  Save
} from 'lucide-react';
import { logActivity } from '@/lib/logger';

interface GalleryItem {
  id: string;
  name: string;
  imageUrl: string;
  categoryId: string;
  createdAt: string;
}

interface GalleryCategory {
  id: string;
  name: string;
  parentId: string | null;
}

export default function GalleryManagementPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [categories, setCategories] = useState<GalleryCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingItem, setEditingItem] = useState<GalleryItem | null>(null);
  
  // Form state
  const [newName, setNewName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    const qPhotos = query(collection(db, 'gallery'), orderBy('createdAt', 'desc'));
    const unsubPhotos = onSnapshot(qPhotos, (snapshot) => {
      const galleryData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as GalleryItem[];
      setItems(galleryData);
    });

    const qCat = query(collection(db, 'gallery_categories'), orderBy('name'));
    const unsubCat = onSnapshot(qCat, (snapshot) => {
      const catData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as GalleryCategory[];
      setCategories(catData);
      setLoading(false);
    });

    return () => {
      unsubPhotos();
      unsubCat();
    };
  }, []);

  const handleOpenModal = (item?: GalleryItem) => {
    if (item) {
      setEditingItem(item);
      setNewName(item.name);
      setSelectedCategoryId(item.categoryId || '');
      setPreviewUrl(item.imageUrl);
      setSelectedFile(null);
    } else {
      setEditingItem(null);
      setNewName('');
      setSelectedCategoryId('');
      setPreviewUrl(null);
      setSelectedFile(null);
    }
    setIsModalOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const getCategoryName = (catId: string): string => {
    const cat = categories.find(c => c.id === catId);
    return cat ? cat.name : 'Unknown';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !selectedCategoryId) {
      alert("Please fill name and category.");
      return;
    }

    setUploading(true);
    try {
      let finalImageUrl = previewUrl;

      // If a new file was selected, upload it
      if (selectedFile) {
        const storageRef = ref(storage, `gallery/${Date.now()}_${selectedFile.name}`);
        const snapshot = await uploadBytes(storageRef, selectedFile);
        finalImageUrl = await getDownloadURL(snapshot.ref);
      }

      if (editingItem) {
        // Update Existing
        await updateDoc(doc(db, 'gallery', editingItem.id), {
          name: newName,
          imageUrl: finalImageUrl,
          categoryId: selectedCategoryId
        });
        await logActivity('edit', 'gallery', `Edited: ${newName}`);
      } else {
        // Create New
        await addDoc(collection(db, 'gallery'), {
          name: newName,
          imageUrl: finalImageUrl,
          categoryId: selectedCategoryId,
          createdAt: new Date().toISOString()
        });
        await logActivity('create', 'gallery', `New photo: ${newName}`);
      }

      setIsModalOpen(false);
    } catch (error) {
      console.error("Save error:", error);
      alert("Error saving gallery item");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    const item = items.find(i => i.id === id);
    if (window.confirm('Remove this photo?')) {
      await deleteDoc(doc(db, 'gallery', id));
      if (item) {
        await logActivity('delete', 'gallery', item.name);
      }
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('admin_gallery_title')}</h1>
          <p className="text-white/50 text-sm">{t('admin_gallery_sub')}</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="bg-[#F57F25] hover:bg-[#e06d15] text-black font-black px-6 py-3 rounded-2xl flex items-center gap-2 transition-all uppercase text-sm shadow-lg shadow-[#F57F25]/20"
        >
          <Plus size={20} />
          {t('admin_gallery_add')}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-[#F57F25]" size={48} />
        </div>
      ) : categories.length === 0 ? (
        <div className="bg-[#111] border border-white/5 rounded-3xl p-20 flex flex-col items-center justify-center text-center">
          <Tag size={48} className="text-[#F57F25] mb-4" />
          <h3 className="text-white font-black text-lg uppercase mb-2">{t('admin_gallery_no_cat')}</h3>
          <p className="text-white/40 text-sm max-w-xs">{t('admin_gallery_no_cat_sub')}</p>
        </div>
      ) : (
        <div className="space-y-12">
          
          {/* Uncategorized Photos */}
          {items.filter(item => !item.categoryId || !categories.find(c => c.id === item.categoryId)).length > 0 && (
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <div className="w-2 h-8 bg-white/20 rounded-full"></div>
                <h2 className="text-xl font-black text-white/40 uppercase tracking-widest italic">{t('admin_gallery_uncategorized')}</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {items.filter(item => !item.categoryId || !categories.find(c => c.id === item.categoryId)).map((item) => (
                  <div key={item.id} className="group relative bg-[#111] rounded-3xl overflow-hidden border border-white/5 aspect-square border-dashed">
                    <img src={item.imageUrl} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" alt={item.name} />
                    <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                      <button onClick={() => handleOpenModal(item)} className="p-3 bg-white text-black rounded-full hover:bg-[#F57F25] transition-colors">
                        <Pencil size={20} />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="p-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors">
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Categorized Photos */}
          {categories.map(category => {
            const catItems = items.filter(item => item.categoryId === category.id);
            if (catItems.length === 0) return null;

            return (
              <div key={category.id} className="space-y-6">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-8 bg-[#F57F25] rounded-full"></div>
                  <h2 className="text-xl font-black text-white uppercase tracking-widest">{getCategoryName(category.id)}</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                  {catItems.map((item) => (
                    <div key={item.id} className="group relative bg-[#111] rounded-3xl overflow-hidden border border-white/5 aspect-square">
                      <img src={item.imageUrl} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" alt={item.name} />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                        <button onClick={() => handleOpenModal(item)} className="p-3 bg-white text-black rounded-full hover:bg-[#F57F25] transition-colors">
                          <Pencil size={20} />
                        </button>
                        <button onClick={() => handleDelete(item.id)} className="p-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors">
                          <Trash2 size={20} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] w-full max-w-lg rounded-[40px] border border-white/10 shadow-2xl overflow-hidden p-8 md:p-12">
            <div className="flex justify-between items-center mb-10">
              <h2 className="text-2xl font-black text-white uppercase tracking-tight">
                {editingItem ? t('admin_gallery_edit') : t('admin_gallery_publish')}
              </h2>
              <button onClick={() => setIsModalOpen(false)} className="text-white/40 hover:text-white transition-colors">
                <X size={28} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="space-y-2">
                <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_gallery_photo_title')}</label>
                <input
                  type="text"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none transition-all"
                  placeholder={t('admin_gallery_placeholder')}
                  required
                />
              </div>

              <div className="space-y-2">
                <label className="text-white/50 text-[10px] font-black uppercase tracking-widest ml-1">{t('admin_gallery_category')}</label>
                <select
                  value={selectedCategoryId}
                  onChange={e => setSelectedCategoryId(e.target.value)}
                  className="w-full bg-black border border-white/10 rounded-2xl py-4 px-6 text-white focus:border-[#F57F25] outline-none appearance-none cursor-pointer"
                  required
                >
                  <option value="" disabled>{t('admin_gallery_select_cat')}</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {getCategoryName(cat.id)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-4">
                <div className="relative aspect-video bg-black border-2 border-dashed border-white/10 rounded-3xl overflow-hidden group hover:border-[#F57F25]/50 transition-colors">
                  {previewUrl ? (
                    <>
                      <img src={previewUrl} className="w-full h-full object-cover" alt="Preview" />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                         <label htmlFor="gallery-upload" className="cursor-pointer bg-white text-black font-bold px-6 py-2 rounded-full text-xs uppercase">{t('admin_gallery_change')}</label>
                      </div>
                    </>
                  ) : (
                    <label htmlFor="gallery-upload" className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer hover:bg-white/5 transition-colors">
                      <Upload className="text-[#F57F25] mb-4" size={40} />
                      <span className="text-white/40 text-xs font-bold uppercase tracking-widest">{t('admin_gallery_select_file')}</span>
                    </label>
                  )}
                  <input 
                    type="file" 
                    id="gallery-upload" 
                    className="hidden" 
                    accept="image/*" 
                    onChange={handleFileChange}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={uploading}
                className="w-full bg-[#F57F25] hover:bg-[#e06d15] text-black font-black py-5 rounded-[20px] transition-all flex items-center justify-center gap-3 uppercase text-sm shadow-xl shadow-[#F57F25]/20 disabled:opacity-50"
              >
                {uploading ? <Loader2 className="animate-spin" size={20} /> : <><Save size={20} /> {editingItem ? t('admin_gallery_save') : t('admin_gallery_publish')}</>}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
